<template lang="html">
  <div>
    <div v-if="exportable" class="d-flex justify-content-end my-2 px-2">
      <d-button
        v-if="isSelectionMode && selectedPuzzles.length > 0"
        type="button"
        class="btn btn-success text-white mr-2"
        @click="updateStatusInBatch"
      >
        <i class="fas fa-check mr-1" />Concluir ({{ selectedPuzzles.length }})
      </d-button>
      <d-button
        v-if="isSelectionMode && selectedPuzzles.length > 0"
        type="button"
        class="btn btn-primary text-white mr-2"
        @click="openExportModal"
      >
        <i class="fas fa-download mr-1" />Exportar ({{
          selectedPuzzles.length
        }})
      </d-button>
      <d-button
        v-if="puzzles.length > 0"
        type="button"
        :class="isSelectionMode ? 'btn btn-secondary' : 'btn btn-primary'"
        class="text-white"
        @click="toggleSelectionMode"
      >
        {{ isSelectionMode ? 'Cancelar' : 'Selecionar jogos' }}
      </d-button>
    </div>

    <table class="table mb-0 table-striped">
      <thead class="bg-light">
        <tr>
          <th
            v-if="exportable && isSelectionMode"
            scope="col"
            class="td-check border-0"
          >
            <d-form-checkbox
              v-model="selectAll"
              :indeterminate="isIndeterminate"
              @change="toggleSelectAll"
              class="m-0"
            >
            </d-form-checkbox>
          </th>
          <th scope="col" class="border-0">#</th>
          <th scope="col" class="border-0">Nome</th>
          <th v-if="highlightTemplate" scope="col" class="border-0">
            Template
          </th>
          <th v-if="showStatus" scope="col" class="border-0">
            Status
          </th>
          <th v-if="!isSelectionMode" scope="col" class="border-0">Opções</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, index) in puzzles" :key="index">
          <td v-if="exportable && isSelectionMode" class="align-middle">
            <d-form-checkbox
              v-model="selectedPuzzles"
              :value="row.id"
              class="m-0"
            >
            </d-form-checkbox>
          </td>
          <td class="align-middle">{{ row.id }}</td>
          <td class="td-4 link align-middle">
            <slot
              name="actions"
              :puzzleId="row.id"
              :puzzleName="row.name"
              :hasChildren="row.has_grouped_generation"
            />
          </td>
          <td v-if="highlightTemplate" class="align-middle">
            {{ row.template_name || '' }}
          </td>
          <td v-if="showStatus" class="align-middle">
            {{ getPuzzleStatusLabel(row.status) }}
          </td>
          <td v-if="!isSelectionMode" class="td-2 align-middle">
            <puzzle-buttons :puzzle="row" :eventName="eventName" />
          </td>
        </tr>
        <tr v-if="puzzles.length === 0">
          <td :colspan="emptyColspan" class="no-content">
            Ainda não tem nenhum jogo cadastrado.
          </td>
        </tr>
      </tbody>
      <confirmation-modal
        :cancel="close"
        :confirm="deleteRow"
        :title="modalTitle"
        :message="modalMessage"
      />
    </table>

    <export-file-modal v-if="exportable" gameType="crossword" :items="selectedPuzzleData" />

  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Events from '_utils/events';
import ConfirmationModal from '_shared/ConfirmationModal.vue';
import { getPuzzleStatusLabel, STATUS } from '_modules/puzzle/components/PuzzleModel';
import PuzzleButtons from './PuzzleButtons.vue';
import PuzzleDelete from '../mixins/PuzzleDelete';
import ExportFileModal from './ExportFileModal.vue';

export default {
  name: 'puzzle-list-table',
  mixins: [PuzzleDelete],
  components: {
    ConfirmationModal,
    PuzzleButtons,
    ExportFileModal,
  },
  props: {
    puzzles: {
      type: Array,
      default() {
        return [];
      },
    },
    eventName: {
      type: String,
      default: null,
    },
    highlightTemplate: {
      type: Boolean,
      default: false,
    },
    exportable: {
      type: Boolean,
      default: false,
    },
    showStatus: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectPuzzle: null,
      modalTitle: '',
      modalMessage: '',
      isSelectionMode: false,
      selectedPuzzles: [],
      selectAll: false,
    };
  },
  computed: {
    isIndeterminate() {
      return (
        this.selectedPuzzles.length > 0 &&
        this.selectedPuzzles.length < this.puzzles.length
      );
    },
    emptyColspan() {
      let colspan = 3;

      if (this.highlightTemplate) colspan += 1;
      if (this.showStatus) colspan += 1;

      return colspan;
    },
    selectedPuzzleData() {
      const ids = this.selectedPuzzles;
      return this.puzzles.filter(p => ids.indexOf(p.id) !== -1);
    },
  },
  methods: {
    ...mapActions(['setToggle', 'activeAlert']),
    getPuzzleStatusLabel,
    toggleSelectionMode() {
      this.isSelectionMode = !this.isSelectionMode;
      if (!this.isSelectionMode) {
        this.selectedPuzzles = [];
        this.selectAll = false;
      }
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedPuzzles = [];
      } else {
        this.selectedPuzzles = this.puzzles.map(p => p.id);
      }
    },
    openExportModal() {
      if (this.selectedPuzzles.length === 0) {
        this.activeAlert({
          message: 'Nenhum jogo selecionado',
          theme: 'warning',
        });
        return;
      }
      this.setToggle({ type: 'export_file_modal', bool: true });
    },
    updateStatusInBatch() {
      if (this.selectedPuzzles.length === 0) return;

      this.$emit('updateStatusBatch', {
        ids: this.selectedPuzzles,
        status: STATUS.COMPLETED 
      });

      this.toggleSelectionMode();
    },
  },
  watch: {
    puzzles() {
      if (this.isSelectionMode) {
        this.selectedPuzzles = [];
        this.selectAll = false;
      }
    },
  },
  mounted() {
    Events.$on(this.eventName, puzzle => {
      this.selectPuzzle = puzzle.id;
      const label = puzzle.is_template ? 'template' : 'jogo';

      this.modalTitle = `Confirme exclusão do ${label}`;
      this.modalMessage = `Tem certeza que deseja excluir o ${label} "${puzzle.name}"?`;
    });
  },
  destroyed() {
    Events.$off(this.eventName);
  },
};
</script>

<style lang="scss" scoped>
@import '~_scss/overrides';

.link {
  color: $primary;
  a {
    cursor: pointer;
  }
}

.td {
  &-check {
    width: 40px;
  }
  &-2 {
    min-width: 140px;
  }
  &-3 {
    min-width: 140px;
  }
  &-4 {
    min-width: 200px;
  }
}

.align-middle {
  vertical-align: middle;
}
</style>
